import { type DriveFile } from '@/shared/DriveTypes';
import { ExpandMore } from '@mui/icons-material';
import { CircularProgress, Collapse } from '@mui/material';
import cx from 'classnames';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { Activity, useLayoutEffect, useRef } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { Link } from 'wouter';
import Icon from '~/elements/Icon/Icon';
import { isBun, LEGACY, maxDepth, newFileId, PROD, search } from '~/utils/constants';
import { concat } from '~/utils/fast';
import gup from '~/utils/gup';
import { TreeMenuContext } from '~/utils/hooks/hooks';
import { isMarkdownLink } from '~/utils/isMarkdownLink';
import { clearPrefetchTimeout, prefetch, prefetchTimeout } from '~/utils/prefetch';
import { sort } from '~/utils/sort';
import { useOpenId, useStore, useTheme } from '~/utils/store';
import { colorIsDark, getId, isAFolder, isAShortcut, legacyClick } from '~/utils/utils';
import { EditableTreeItem } from './EditableTreeItem';
import getAllChildren from './getAllChildren';
import { HomeIcon } from './HomeIcon';
import { TreeMenuButtons } from './MenuButtons';
import * as styles from './Tree.css';
import { TreeMenu } from './TreeMenu';
import { iconTypes } from './treeMenuUtils';

const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  legacyClick?.(e);
  const { isTablet, setSidebarOpen } = useStore.getState();
  if (isTablet) setSidebarOpen(false);
};

type MenuState = Record<string, boolean>;

const baseDepth = 26.5;
const getPaddingLeft = (depth: number) => (depth === 1 ? baseDepth : baseDepth + (depth - 1) * 19.1565);

const Tree = ({
  files,
  wiki,
  wikis,
  // Re-add once react fixes
  // https://playground.react.dev/#N4Igzg9grgTgxgUxALhAMygOzgFwJYSYAEAKgBTBEQA0RaeAJkQLxUB0jtjLd3AvgEpgMBDljEAPAD4JAeil8QfIA
  // folderId = wiki.id
  folderId: _folderId,
  id: _id = _folderId,
  depth = 0,
  closedByDefault = false,
  showHome = false,
}: {
  files: DriveFile[];
  wiki: DriveFile;
  wikis: DriveFile[];
  folderId?: string;
  id?: string | undefined;
  depth?: number;
  closedByDefault?: boolean;
  showHome?: boolean;
}) => {
  const { cache } = useSWRConfig();
  const { data: menuState, mutate: mutateMenu } = useSWR<MenuState>(`/api/menu/${wiki.id}`);
  const { openId } = useOpenId('tree');
  const theme = useTheme();
  const status = useStore((state) => state.status);
  const statusIds = useStore((state) => state.statusIds);
  const linkRef = useRef<HTMLAnchorElement>(null);

  // Remove once react fixes
  // https://playground.react.dev/#N4Igzg9grgTgxgUxALhAMygOzgFwJYSYAEAKgBTBEQA0RaeAJkQLxUB0jtjLd3AvgEpgMBDljEAPAD4JAeil8QfIA
  const folderId = _folderId ?? wiki.id;
  const id = _id ?? folderId;
  const { color: bgColor, fontColor } = wiki.properties ?? {};
  const dark = bgColor ? colorIsDark(bgColor, 127) : false;
  const color = theme === 'light' ? (fontColor ?? '#fff') : '#fff';

  if (depth >= maxDepth) throw new Error('Max depth reached');

  // Home link
  if (depth === 0) {
    const currentFile = id === wiki.id ? wiki : (files.find((file) => file.id === id) ?? wiki);
    const isActive = wiki.id === id;
    const canEdit =
      wiki.capabilities?.canEdit || wiki.permissions?.some((p) => p.type === 'anyone' && p.role === 'writer');
    const isLoading = status === 'loading' && statusIds.has(wiki.id);
    return (
      <>
        {showHome && (
          <div
            className={cx(styles.homeLinkContainer, {
              [styles.active]: isActive,
              [styles.menuOpen]: openId === wiki.id,
              edit: canEdit,
              dark,
            })}
          >
            <Link
              className={cx(styles.link, styles.folder)}
              to={LEGACY ? `/app/page/${wiki.id}?p=${wiki.id}` : `/app/page/${wiki.id}/${wiki.id}${search}`}
              {...(isActive && { 'aria-current': 'page', ref: linkRef })}
              style={{ color: theme === 'light' ? fontColor : undefined }}
              onClick={handleLinkClick}
            >
              {isLoading && <CircularProgress style={{ color, margin: '3px 6px 3px 0px' }} thickness={4} size={16} />}
              <Activity mode={isLoading ? 'hidden' : 'visible'}>
                <HomeIcon wiki={wiki} />
              </Activity>
              {isActive && canEdit ? (
                <EditableTreeItem linkRef={linkRef} file={wiki} files={files} wiki={wiki}>
                  {wiki.name}
                </EditableTreeItem>
              ) : (
                <span className={styles.linkText}>{wiki.name}</span>
              )}
            </Link>
            {canEdit && <TreeMenuButtons wiki={wiki} file={wiki} />}
          </div>
        )}
        <div role="tree">
          <Tree
            files={files}
            wiki={wiki}
            wikis={wikis}
            id={currentFile.id}
            depth={depth + 1}
            closedByDefault={closedByDefault}
          />
        </div>
        <TreeMenu currentFile={currentFile} files={files} wiki={wiki} closedByDefault={closedByDefault} wikis={wikis} />
      </>
    );
  }

  const paddingLeft = getPaddingLeft(depth);
  const children = sort(files.filter((file) => file.parents?.[0] === folderId && !file.trashed));
  return (
    <>
      {children.map((file) => {
        const isDuplicate = isAShortcut(file) ? files.some((o) => o.id === getId(file)) : false;
        const isOpen = menuState?.[file.id] ?? !closedByDefault;
        const isFolder = isAFolder(file);
        const hasChildren = isFolder ? files.some((o) => o.parents?.[0] === getId(file)) : false;
        const isFolderWithChildren = !isDuplicate && hasChildren;
        const isEmptyFolder = isFolder && !isFolderWithChildren;
        const isActive = id === file.id;
        const legacyParentId = LEGACY ? gup('p') : undefined;
        const legacyTo = LEGACY
          ? legacyParentId
            ? `/app/page/${file.id}?p=${legacyParentId}`
            : `/app/page/${file.id}`
          : '';

        const externalLink = isMarkdownLink(file);
        const isLoading = status === 'loading' && statusIds.has(file.id);
        const canEdit = file.capabilities?.canEdit;

        return (
          <div key={file.id} role="treeitem">
            <div
              className={cx(styles.linkContainer, {
                [styles.active]: isActive,
                [styles.menuOpen]: openId === file.id,
                dark,
                edit: canEdit,
              })}
              {...(!isFolder && {
                onMouseDown: () => prefetch(file, cache),
                // hover prefetch
                ...(PROD && {
                  onMouseEnter: () => prefetchTimeout(file, cache, 200),
                  onMouseLeave: () => clearPrefetchTimeout(file),
                }),
              })}
            >
              <Link
                className={cx(styles.link, {
                  [styles.folder]: isFolderWithChildren,
                  [styles.emptyFolder]: isEmptyFolder,
                })}
                to={externalLink ? externalLink.url : LEGACY ? legacyTo : `/app/page/${wiki.id}/${file.id}${search}`}
                {...(isActive && { 'aria-current': 'page', ref: linkRef })}
                {...(externalLink?.url && { target: '_blank' })}
                onClick={handleLinkClick}
                style={{ paddingLeft, color: theme === 'light' ? fontColor : undefined }}
              >
                {isFolderWithChildren && (
                  <>
                    {isLoading && (
                      <CircularProgress
                        style={{ color, marginRight: '.2rem' }}
                        thickness={4}
                        size={16}
                        onClick={(e) => {
                          e.preventDefault();
                          const button = e.currentTarget.nextElementSibling;
                          if (!(button instanceof HTMLButtonElement)) throw new Error('no button found');
                          button.click();
                        }}
                      />
                    )}
                    <Activity mode={isLoading ? 'hidden' : 'visible'}>
                      <button
                        type="button"
                        className={styles.expandMoreButton}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (e.shiftKey && (e.metaKey || e.ctrlKey)) {
                            const folders = files.filter(isAFolder);
                            const newMenuState = Object.fromEntries(folders.map((file) => [file.id, !isOpen]));
                            void mutateMenu({ ...menuState, ...newMenuState }, false);
                          } else if (e.shiftKey) {
                            const nestedChildren = concat([file], getAllChildren(files, file.id).filter(isAFolder));
                            const newMenuState = Object.fromEntries(nestedChildren.map((file) => [file.id, !isOpen]));
                            void mutateMenu({ ...menuState, ...newMenuState }, false);
                          } else {
                            void mutateMenu({ ...menuState, [file.id]: !isOpen }, true);
                          }
                        }}
                      >
                        <ExpandMore
                          titleAccess="Click to expand more"
                          style={{
                            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                            color: (theme === 'light' ? fontColor : undefined) ?? '#fff',
                            height: 24,
                          }}
                        />
                      </button>
                    </Activity>
                  </>
                )}
                {!isFolderWithChildren && (
                  <>
                    {isLoading && (
                      <CircularProgress
                        style={{
                          color,
                          marginLeft: isFolder ? 0 : 4,
                          marginRight: isFolder ? 3 : 4,
                        }}
                        thickness={4}
                        size={16}
                      />
                    )}
                    <Activity mode={isLoading ? 'hidden' : 'visible'}>
                      <Icon
                        file={file}
                        bgColor={bgColor}
                        color={theme === 'light' ? fontColor : undefined}
                        externalLink={externalLink}
                        sidebar
                      />
                    </Activity>
                  </>
                )}
                {isActive && canEdit ? (
                  <EditableTreeItem linkRef={linkRef} file={file} files={files} wiki={wiki}>
                    {externalLink ? externalLink.name : file.name}
                  </EditableTreeItem>
                ) : (
                  <span className={styles.linkText}>{externalLink ? externalLink.name : file.name}</span>
                )}
              </Link>
              {canEdit && <TreeMenuButtons wiki={wiki} file={file} />}
            </div>
            {isFolderWithChildren && (
              <Collapse in={isOpen} timeout={isBun ? 0 : 'auto'} unmountOnExit>
                <Tree
                  files={files}
                  wiki={wiki}
                  wikis={wikis}
                  id={id}
                  folderId={getId(file)}
                  depth={depth + 1}
                  closedByDefault={closedByDefault}
                />
              </Collapse>
            )}
          </div>
        );
      })}
    </>
  );
};

const TreeContainer = (props: Parameters<typeof Tree>[0]) => {
  const addMenu = usePopupState({ variant: 'popover', popupId: 'addMenu' });
  const moreMenu = usePopupState({ variant: 'popover', popupId: 'moreMenu' });

  // Update menu anchor from optimistic newFileId to file.id after file creation
  useLayoutEffect(() => {
    if (!props.id) return;
    if (props.id.startsWith(newFileId)) return;
    const menu = addMenu.isOpen ? addMenu : moreMenu.isOpen ? moreMenu : null;
    const type = addMenu.isOpen ? iconTypes.add : moreMenu.isOpen ? iconTypes.more : null;
    if (!menu) return;

    const buttonId = (menu.anchorEl as HTMLElement | undefined)?.dataset.id;
    if (!buttonId?.startsWith(newFileId)) return;

    const button = document.querySelector(`button[data-id='${props.id}'][data-icon='${type}']`);
    if (!(button instanceof HTMLButtonElement)) return;
    menu.setAnchorEl(button);

    // focus the element in the menu with `.Mui-focusVisible`
    const menuItem = document.querySelector('[role="presentation"] [role="menu"] [role="menuitem"].Mui-focusVisible');
    if (menuItem instanceof HTMLElement) menuItem.focus();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  return (
    <TreeMenuContext.Provider value={{ addMenu, moreMenu }}>
      <Tree {...props} />
    </TreeMenuContext.Provider>
  );
};
export default TreeContainer;
